import { ref } from 'vue';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Make Pusher available globally (required by Laravel Echo)
window.Pusher = Pusher;

// Shared Echo instance
let echoInstance = null;

/**
 * Composable for Laravel Echo with Reverb WebSocket
 * Provides methods for subscribing to channels and receiving real-time events
 */
export function useEcho() {
    const connected = ref(false);

    /**
     * Initialize Echo connection to Reverb
     */
    const initEcho = () => {
        if (echoInstance) {
            return echoInstance;
        }

        echoInstance = new Echo({
            broadcaster: 'reverb',
            key: process.env.VUE_APP_REVERB_APP_KEY,
            wsHost: process.env.VUE_APP_REVERB_HOST || 'localhost',
            wsPort: parseInt(process.env.VUE_APP_REVERB_PORT) || 8080,
            wssPort: parseInt(process.env.VUE_APP_REVERB_PORT) || 8080,
            forceTLS: process.env.VUE_APP_REVERB_SCHEME === 'https',
            enabledTransports: ['ws', 'wss'],
            disableStats: true,
        });

        // Track connection state
        echoInstance.connector.pusher.connection.bind('connected', () => {
            connected.value = true;
            console.log('Reverb WebSocket connected');
        });

        echoInstance.connector.pusher.connection.bind('disconnected', () => {
            connected.value = false;
            console.log('Reverb WebSocket disconnected');
        });

        return echoInstance;
    };

    /**
     * Get Echo instance (initializes if needed)
     */
    const getEcho = () => {
        if (!echoInstance) {
            initEcho();
        }
        return echoInstance;
    };

    /**
     * Subscribe to a public channel
     * @param {string} channelName 
     * @returns {object} Channel instance
     */
    const channel = (channelName) => {
        return getEcho().channel(channelName);
    };

    /**
     * Subscribe to a private channel (requires authentication)
     * @param {string} channelName 
     * @returns {object} Channel instance
     */
    const privateChannel = (channelName) => {
        return getEcho().private(channelName);
    };

    /**
     * Subscribe to a channel and listen for an event
     * @param {string} channelName 
     * @param {string} eventName 
     * @param {Function} callback 
     * @returns {object} Channel instance
     */
    const listen = (channelName, eventName, callback) => {
        return getEcho().channel(channelName).listen(eventName, callback);
    };

    /**
     * Subscribe to a private channel and listen for an event
     * @param {string} channelName 
     * @param {string} eventName 
     * @param {Function} callback 
     * @returns {object} Channel instance
     */
    const listenPrivate = (channelName, eventName, callback) => {
        return getEcho().private(channelName).listen(eventName, callback);
    };

    /**
     * Leave a channel
     * @param {string} channelName 
     */
    const leave = (channelName) => {
        getEcho().leave(channelName);
    };

    /**
     * Disconnect from WebSocket
     */
    const disconnect = () => {
        if (echoInstance) {
            echoInstance.disconnect();
            echoInstance = null;
            connected.value = false;
        }
    };

    return {
        // State
        connected,

        // Methods
        initEcho,
        getEcho,
        channel,
        privateChannel,
        listen,
        listenPrivate,
        leave,
        disconnect,
    };
}

export default useEcho;
